// attribution-state.js — pure helpers for the visitor-attribution cookie.
// NO DOM / browser globals here (keeps it unit-testable under `node --test`).
//
// Contract (read by app.dauberhq.com at registration — see docs/attribution-cookie.md):
//   cookie  dhq_attribution, Domain=.dauberhq.com, Path=/, 30 days, SameSite=Lax
//   value   encodeURIComponent(JSON.stringify({ params, landing, referrer, ts }))

export var COOKIE_NAME = 'dhq_attribution';
export var MAX_AGE_DAYS = 30;

var MAX_PARAMS = 20;
var MAX_KEY_LEN = 64;
var MAX_VALUE_LEN = 256;
var MAX_COOKIE_LEN = 3000; // encoded value; headroom under the ~4KB per-cookie limit

// Never persisted: Craft internals, on-site search terms, and anything that
// could carry credentials or personal data.
var DENYLIST = [
  'p', 'token', 'site', 'x-craft-preview', 'x-craft-live-preview',
  'q', 'query', 'search', 'page',
  'email', 'password', 'code', 'state',
];

// Query string → { key: value } of the params worth keeping. First value wins
// for repeated keys; empty values and denylisted keys are dropped.
export function pickParams(search) {
  var out = {};
  var count = 0;
  new URLSearchParams(search || '').forEach(function (value, key) {
    var k = key.trim().toLowerCase();
    var v = value.trim();
    if (!k || !v || count >= MAX_PARAMS) return;
    if (k.length > MAX_KEY_LEN || DENYLIST.indexOf(k) !== -1) return;
    if (Object.prototype.hasOwnProperty.call(out, k)) return;
    out[k] = v.slice(0, MAX_VALUE_LEN);
    count++;
  });
  return out;
}

// Referrer only when it's off-site — internal navigation tells us nothing.
export function externalReferrer(referrer, hostname) {
  if (!referrer) return '';
  try {
    var host = new URL(referrer).hostname;
    if (host === hostname || isOwnDomain(host)) return '';
    return referrer.slice(0, MAX_VALUE_LEN);
  } catch (e) {
    return '';
  }
}

function isOwnDomain(host) {
  return host === 'dauberhq.com' || /\.dauberhq\.com$/.test(host);
}

// Share across *.dauberhq.com so the app can read it; host-only elsewhere (ddev).
export function cookieDomain(hostname) {
  return isOwnDomain(hostname) ? '.dauberhq.com' : '';
}

// Decide what (if anything) to write for this page view.
//   - URL carries params      → new touch, overwrite (last tagged touch wins)
//   - no params, no cookie, external referrer → record the organic/referral touch
//   - otherwise               → leave the stored attribution alone (null)
export function nextAttribution(opts) {
  var params = pickParams(opts.search);
  var referrer = externalReferrer(opts.referrer, opts.hostname);
  var hasParams = Object.keys(params).length > 0;

  if (!hasParams && (opts.existing || !referrer)) return null;

  return {
    params: params,
    landing: (opts.pathname || '/').slice(0, MAX_VALUE_LEN),
    referrer: referrer,
    ts: opts.now || new Date().toISOString(),
  };
}

// Encode for the cookie, dropping params from the end until it fits.
export function encodeAttribution(data) {
  var copy = { params: Object.assign({}, data.params), landing: data.landing, referrer: data.referrer, ts: data.ts };
  var keys = Object.keys(copy.params);
  var encoded = encodeURIComponent(JSON.stringify(copy));
  while (encoded.length > MAX_COOKIE_LEN && keys.length) {
    delete copy.params[keys.pop()];
    encoded = encodeURIComponent(JSON.stringify(copy));
  }
  return encoded;
}

export function decodeAttribution(raw) {
  if (!raw) return null;
  try {
    var data = JSON.parse(decodeURIComponent(raw));
    return data && typeof data === 'object' && data.params ? data : null;
  } catch (e) {
    return null;
  }
}

// Pull one cookie's raw value out of a document.cookie string.
export function readCookie(cookieString, name) {
  var parts = (cookieString || '').split(';');
  for (var i = 0; i < parts.length; i++) {
    var c = parts[i].trim();
    if (c.indexOf(name + '=') === 0) return c.slice(name.length + 1);
  }
  return '';
}
